export interface IProject {
  id: number;
  name: string;
  createdAt: string;
}

export interface ISession {
  id: number;
  projectId: number;
  title: string;
  model: string;
  createdAt: string;
}

export const AVAILABLE_MODELS = [
  { id: 'gpt-5.6-sol', label: 'GPT-5.6 Sol', abbr: '5.6 Sol', hint: 'Codex CLI — highest-capability coding model' },
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', abbr: 'O 5.5', hint: 'Default — best quality per token (effort medium)' },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', abbr: 'S 5', hint: 'Fast and capable — best for most tasks' },
  { id: 'claude-opus-5', label: 'Claude Opus 5', abbr: 'O 5', hint: 'Previous Opus generation — slower, more tokens' },
  { id: 'claude-opus-4-8', label: 'Claude Opus 4.8', abbr: 'O 4.8', hint: 'Previous Opus generation' },
  { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', abbr: 'H 4.5', hint: 'Fastest and cheapest — good for simple tasks' },
] as const;

// A/B 23 sep 2026 (CM3-METHOD/ab-opus5): Opus 5.5 at effort medium matches or beats
// Opus 5 high on checks, gate discipline and naturalness, at half the clock and ~25% fewer tokens.
export const DEFAULT_MODEL = 'claude-opus-5-5';

// Session-length advisory thresholds, in absolute context tokens.
// Long-context quality degrades with absolute token count ("context rot"),
// not with the fill fraction of the 1M window. Benchmarks (MRCR/GraphWalks
// system-card data) hold near-ceiling through ~128-256k; reasoning-style
// tasks drift from ~128k and degradation is clearly felt around 300-400k.
// 150k also matches Anthropic's server-side compaction default trigger.
export const CONTEXT_WARN_TOKENS = 150000;
export const CONTEXT_HIGH_TOKENS = 350000;

// Real context window per model. Smaller-window models (Haiku) hit lossy
// auto-compaction long before the absolute thresholds above, so thresholds
// are capped at a fraction of the window (50% warn / 75% high).
export const MODEL_CONTEXT_WINDOWS: Record<string, number> = {
  'gpt-5.6-sol': 258400,
  'claude-opus-5-5': 1000000,
  'claude-opus-5': 1000000,
  'claude-opus-4-8': 1000000,
  'claude-sonnet-5': 1000000,
  'claude-haiku-4-5-20251001': 200000,
};

export interface IToolActivity {
  toolUseId: string;
  toolName: string;
  input: Record<string, any>;
  status: 'running' | 'completed';
  summary?: string;
  elapsedSeconds?: number;
}

export type IContentBlock =
  | { type: 'text'; text: string }
  | { type: 'activity'; toolUseId: string };

export interface IChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  activities?: IToolActivity[];
  contentBlocks?: IContentBlock[];
  attachmentPreviews?: string[];
  timestamp?: number;
}

export interface IFileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  size?: number;
  modifiedAt?: string;
  children?: IFileNode[];
}

export type ITabType = 'session' | 'file';

export interface ITab {
  id: string;
  type: ITabType;
  label: string;
  sessionId?: number;
  filePath?: string;
  projectId?: number;
}

export interface IAiAssistantState {
  projects: IProject[];
  selectedProjectId: number | null;
  sessions: ISession[];
  tabs: ITab[];
  activeTabId: string | null;
  fileTree: IFileNode[];
  messagesBySession: Record<number, IChatMessage[]>;
  streamingSessionId: number | null;
  fileContents: Record<string, string>;
  loadingProjects: boolean;
  loadingSessions: boolean;
  loadingFiles: boolean;
  agentFileChangeSeq: number;
  unreadSessionIds: number[];
  favoriteSessionsByProject: Record<number, number[]>;
}

export type IAiAssistantAction =
  | { type: 'SET_PROJECTS'; projects: IProject[] }
  | { type: 'ADD_PROJECT'; project: IProject }
  | { type: 'UPDATE_PROJECT'; project: IProject }
  | { type: 'DELETE_PROJECT'; projectId: number }
  | { type: 'SELECT_PROJECT'; projectId: number | null }
  | { type: 'SET_SESSIONS'; sessions: ISession[] }
  | { type: 'ADD_SESSION'; session: ISession }
  | { type: 'UPDATE_SESSION'; session: ISession }
  | { type: 'DELETE_SESSION'; sessionId: number }
  | { type: 'OPEN_TAB'; tab: ITab }
  | { type: 'CLOSE_TAB'; tabId: string }
  | { type: 'SET_ACTIVE_TAB'; tabId: string }
  | { type: 'APPEND_MESSAGE'; sessionId: number; message: IChatMessage }
  | { type: 'APPEND_STREAMING_TEXT'; sessionId: number; text: string }
  | { type: 'SET_STREAMING'; sessionId: number | null }
  | { type: 'SET_FILE_TREE'; fileTree: IFileNode[] }
  | { type: 'SET_FILE_CONTENT'; path: string; content: string }
  | { type: 'CLEAR_FILE_CONTENT'; path: string }
  | { type: 'SET_LOADING_PROJECTS'; loading: boolean }
  | { type: 'SET_LOADING_SESSIONS'; loading: boolean }
  | { type: 'SET_LOADING_FILES'; loading: boolean }
  | { type: 'SET_MESSAGES'; sessionId: number; messages: IChatMessage[] }
  | { type: 'RENAME_FILE_PATH'; oldPath: string; newPath: string; newLabel: string }
  | { type: 'ADD_TOOL_ACTIVITY'; sessionId: number; activity: IToolActivity }
  | { type: 'UPDATE_TOOL_SUMMARY'; sessionId: number; toolUseIds: string[]; summary: string }
  | { type: 'UPDATE_TOOL_PROGRESS'; sessionId: number; toolUseId: string; elapsedSeconds: number }
  | { type: 'AGENT_FILES_CHANGED' }
  | { type: 'INIT_FAVORITE_SESSIONS'; favorites: Record<number, number[]> }
  | { type: 'TOGGLE_FAVORITE_SESSION'; sessionId: number; projectId: number }
;
